import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import Form from 'react-jsonschema-form';
import { cloneDeep, get, set } from 'lodash';
import { Button, ButtonGroup } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap/lib';
import { CLIENT_TYPE } from '@upstart/harmony/lib/constants';
import { processForm } from '../../util/jsonSchemaUtils';
import { validateAprConfig } from './validations';
import {
  step1Schema,
  step2Schema,
  getStep3Schema,
  getStep4Schema,
  getStep5Schema,
  step7Schema,
  step8Schema,
  step9Schema,
  getStep10Schema,
} from './Schema';
import {
  step1UiSchema,
  step2UiSchema,
  getStep3UiSchema,
  step4UiSchema,
  step7UiSchema,
  step8UiSchema,
  step9UiSchema,
  step10UiSchema,
} from './uiSchema';
import { PercentField } from '../PercentField';
import AprConfig from './AprConfig';
import './PricingEditor.scss';
import PricingMatrix from '../../containers/PricingMatrix';
import SignAndDrive from '../../containers/SignAndDrive';

const getSchemaList = (formData) => [
  {},
  step1Schema,
  step2Schema,
  getStep3Schema(formData),
  getStep4Schema(formData),
  getStep5Schema(formData),
  step7Schema,
  step8Schema,
  step9Schema,
  getStep10Schema(),
];

const getUiSchemaList = (formData) => [
  {},
  step1UiSchema,
  step2UiSchema,
  getStep3UiSchema(formData),
  step4UiSchema,
  step4UiSchema,
  step7UiSchema,
  step8UiSchema,
  step9UiSchema,
  step10UiSchema,
];

const START_STEP = 1;
const TOTAL_STEPS = 9;
const PRICING_MATRIX_STEPS = [
  1, // online pricing matrix
  2, // instore pricing matrix
  4, // online apr config
  5, // instore apr config
];

const getStep = (step) => step || START_STEP;

function EmbeddedOnlinePricingMatrix() {
  return (
    <PricingMatrix
      embedded
      embeddedStep={1}
      embeddedMaxSteps={TOTAL_STEPS}
      clientType={CLIENT_TYPE.ONLINE}
    />
  );
}

function EmbeddedInStorePricingMatrix() {
  return (
    <PricingMatrix
      embedded
      embeddedStep={2}
      embeddedMaxSteps={TOTAL_STEPS}
      clientType={CLIENT_TYPE.IN_STORE}
    />
  );
}

function OnlineAprConfig(props) {
  return <AprConfig {...props} clientType={CLIENT_TYPE.ONLINE} />;
}

function InStoreAprConfig(props) {
  return <AprConfig {...props} clientType={CLIENT_TYPE.IN_STORE} />;
}

function signAndDrive(props) {
  return <SignAndDrive {...props} />;
}

export default class PricingEditor extends PureComponent {
  constructor(props) {
    super(props);
    const step = getStep(props.step);
    const schemaList = getSchemaList({});
    const uiSchemaList = getUiSchemaList({});
    this.state = processForm(
      schemaList[step],
      uiSchemaList[step],
      schemaList[step],
      uiSchemaList[step],
      this.props.dealer,
    );
  }

  componentDidMount() {
    const { dealer } = this.props;
    if (
      get(dealer, 'featureConfig.disableTaxes') === undefined &&
      get(this.state.formData.taxes, 'cash.state.rate') === undefined
    ) {
      // If the disable taxes config is not yet set
      // and the cash state tax rate has also not been set
      // we can assume its a new dealer
      // we will default disableTaxes config to true
      const formData = cloneDeep(this.state.formData);
      set(formData, 'featureConfig.disableTaxes', true);
      this.setState({ formData });
    }
  }

  componentDidUpdate(prevProps) {
    const { dealer, ymmt } = this.props;
    if (
      getStep(prevProps.step) !== getStep(this.props.step) ||
      dealer !== prevProps.dealer ||
      ymmt !== prevProps.ymmt
    ) {
      const step = getStep(this.props.step);
      const schemaList = getSchemaList(this.state.formData);
      const uiSchemaList = getUiSchemaList(this.state.formData);
      const newState = processForm(
        schemaList[step],
        uiSchemaList[step],
        schemaList[step],
        uiSchemaList[step],
        this.props.dealer,
      );
      this.setState(newState);
    }
  }

  onSubmit = ({ formData }) => {
    const dealer = {
      ...this.state.formData,
      ...formData,
    };

    // remove tax entries with undefined rates
    if (dealer.taxes) {
      const newTaxes = {};
      Object.entries(dealer.taxes).forEach(
        ([financeType, taxesForFinanceType]) => {
          const newTaxesForFinanceType = {};
          Object.entries(taxesForFinanceType).forEach(([taxType, taxEntry]) => {
            if (taxEntry?.rate !== undefined) {
              newTaxesForFinanceType[taxType] = taxEntry;
            }
          });
          newTaxes[financeType] = newTaxesForFinanceType;
          newTaxes[financeType].taxByDealerAddress =
            taxesForFinanceType?.taxByDealerAddress || false;
        },
      );
      dealer.taxes = newTaxes;
    }

    this.props.onSave({
      dealer,
      step: getStep(this.props.step),
      maxSteps: TOTAL_STEPS,
      nextStepOnSuccess: true,
    });
  };

  onBack = () => {
    const { step } = this.props;
    if (step > START_STEP) {
      this.props.setStep(step - 1);
    }
  };

  submitForm = () => {
    if (this.isPricingMatrixStep()) {
      // the pricing matrix has a separate button for saving
      this.props.setStep(this.props.step + 1);
    } else {
      this.form.submit();
    }
  };

  transformErrors = (errors) => {
    // json schema returns a lot of errors
    // this is really not a good experience
    // so filtering only accountNumber related errors
    const marketScanConfigErrors = errors.filter(
      (e) => e.property === '.marketScanConfig.accountNumber',
    );

    if (marketScanConfigErrors) {
      return marketScanConfigErrors;
    }
    // pass through for rest of the cases
    return errors;
  };

  validate = (formData, errors) => {
    const { aprConfig, inStoreAprConfig } = formData;
    if (aprConfig) validateAprConfig(aprConfig, errors.aprConfig);
    if (inStoreAprConfig)
      validateAprConfig(inStoreAprConfig, errors.inStoreAprConfig);

    return errors;
  };

  handleChange = (data) => {
    const schema = { ...this.state.schema };
    const uiSchema = { ...this.state.uiSchema };
    const { formData } = data;
    const { step } = this.props;
    const schemaList = getSchemaList(formData);
    const uiSchemaList = getUiSchemaList(formData);

    if (
      formData?.featureConfig?.disableTaxes &&
      formData.featureConfig.disableTaxes !==
        this.state.formData.featureConfig.disableTaxes
    ) {
      // Store Old Tax config in state in case
      // user decideds to reenable taxes before saving
      const tmpFormData = cloneDeep(formData);
      formData.taxes = {};
      this.setState({ tmpFormData, formData });
    }

    if (
      !formData?.featureConfig?.disableTaxes &&
      formData?.featureConfig?.disableTaxes !==
        this.state.formData?.featureConfig?.disableTaxes &&
      this.state.tmpFormData
    ) {
      formData.taxes = cloneDeep(this.state.tmpFormData?.taxes);
      this.setState({ formData });
    }

    const newState = processForm(
      schema,
      uiSchema,
      schemaList[step],
      uiSchemaList[step],
      formData,
    );

    newState.formData = formData;
    this.setState(newState);
  };

  isPricingMatrixStep = () =>
    PRICING_MATRIX_STEPS.includes(getStep(this.props.step));

  renderNav = () => {
    const schemaList = getSchemaList(this.state.formData);
    return (
      <div>
        <div className="stepwizard">
          <div className="stepwizard-row setup-panel">
            {schemaList.map((schema, index) => {
              if (index === 0) {
                return false;
              }
              return (
                <div key={schema.title} className="pricing-stepwizard-step">
                  {/* eslint-disable-next-line */}
                  <a
                    className={
                      index === getStep(this.props.step)
                        ? 'btn btn-primary btn-circle'
                        : 'btn btn-default btn-circle'
                    }
                    onClick={() => this.props.setStep(index)}
                  >
                    {index}
                  </a>
                  <p>{schema.title}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { fetching, step } = this.props;
    return (
      <div>
        <div
          className={`dealer-editor-container ${
            this.isPricingMatrixStep() ? 'dealer-editor-container-noscroll' : ''
          }`}
        >
          <div>{this.renderNav()}</div>
          <Form
            fields={{
              onlinePricingMatrix: EmbeddedOnlinePricingMatrix,
              instorePricingMatrix: EmbeddedInStorePricingMatrix,
              percent: PercentField,
              onlineAprConfig: OnlineAprConfig,
              inStoreAprConfig: InStoreAprConfig,
              signAndDrive,
            }}
            schema={this.state.schema}
            uiSchema={this.state.uiSchema}
            formData={this.state.formData}
            validate={this.validate}
            transformErrors={this.transformErrors}
            onSubmit={this.onSubmit}
            onChange={this.handleChange}
            enctype="multipart/form-data"
            formContext={{ ymmt: this.props.ymmt }}
            ref={(form) => {
              this.form = form;
            }}
          >
            <button className="hidden" />
          </Form>
          <ButtonGroup>
            {step > START_STEP && (
              <Button
                bsClass="btn btn-default btn-large"
                disabled={fetching}
                onClick={this.onBack}
              >
                {'<< Back'}
              </Button>
            )}
            <LinkContainer to="/dealers">
              <button className="btn btn-default btn-large">
                <span className="glyphicon glyphicon-home" />
              </button>
            </LinkContainer>
            <Button
              bsClass="btn btn-primary btn-large"
              disabled={fetching}
              onClick={this.submitForm}
            >
              {this.isPricingMatrixStep() ? 'Next >>' : 'Save >>'}
            </Button>
          </ButtonGroup>
        </div>
      </div>
    );
  }
}

PricingEditor.propTypes = {
  fetching: PropTypes.bool,
  ymmt: PropTypes.object,
  dealer: PropTypes.object,
  step: PropTypes.number,
  onSave: PropTypes.func,
  setStep: PropTypes.func,
};
